import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { useRoadmapStore } from '../store/roadmapStore';
import { RoadmapFilterOptions } from '../types/roadmap';
import {
  Search,
  Filter,
  TrendingUp,
  User,
  Briefcase,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { ROADMAP_WORKFLOW, getPhaseColor } from '../data/roadmapWorkflow';

export const RoadmapPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    projects,
    filters,
    isLoading,
    error,
    fetchAdvisorProjects,
    setFilters,
    clearFilters
  } = useRoadmapStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchAdvisorProjects();
  }, []);

  const filteredProjects = projects.filter((project) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        project.project_name.toLowerCase().includes(query) ||
        project.owner_name.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    if (filters.status && filters.status.length > 0) {
      if (!filters.status.includes(project.project_status)) return false;
    }

    if (filters.minProgress !== undefined) {
      if (project.progress_percentage < filters.minProgress) return false;
    }

    if (filters.maxProgress !== undefined) {
      if (project.progress_percentage > filters.maxProgress) return false;
    }

    return true;
  });

  const getPhaseStatusColor = (status: 'not_started' | 'in_progress' | 'completed') => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'not_started':
        return 'bg-gray-100 text-gray-500';
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Feuille de route</h1>
              <p className="text-gray-600 mt-1">
                Suivez le parcours d'accompagnement de vos porteurs de projet
              </p>
            </div>
            <button
              onClick={() => fetchAdvisorProjects()}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>

          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un porteur ou un projet..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                showFilters
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtres
            </button>
          </div>

          {showFilters && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Progression minimale
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters.minProgress || 0}
                    onChange={(e) => setFilters({ minProgress: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-sm text-gray-600">{filters.minProgress || 0}%</span>
                </div>
                <div className="md:col-span-2 flex items-end">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun projet trouvé
            </h3>
            <p className="text-gray-600">
              {searchQuery || Object.keys(filters).length > 0
                ? 'Essayez de modifier vos critères de recherche'
                : 'Vous n\'avez pas encore de projets assignés'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredProjects.map((project) => (
              <div
                key={project.project_id}
                onClick={() => navigate(`/roadmap/${project.project_id}`)}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <User className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">
                        {project.owner_name}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">
                      {project.project_name}
                    </h3>
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {project.project_status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400" />
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Progression globale
                    </span>
                    <span className="text-sm font-bold text-blue-600">
                      {project.progress_percentage.toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${project.progress_percentage}%` }}
                    />
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {ROADMAP_WORKFLOW.map((phase) => {
                    const status = project.phase_statuses[phase.key] || 'not_started';
                    return (
                      <span
                        key={phase.key}
                        className={`px-2 py-1 text-xs rounded-full ${getPhaseStatusColor(
                          status
                        )}`}
                      >
                        {phase.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};
